import { defineStore, storeToRefs } from 'pinia';
import http from './http';
import { ref, watch } from 'vue';
import { Response } from '@/types';
import { RouteOption } from '@/router/interface';
import { replaceRoutes, removeRoute } from '@/router/dynamicRoutes';
import { useSettingStore } from './setting';
import { RouteRecordRaw, RouteMeta } from 'vue-router';
import { useAuthStore } from '@/plugins';
import router from '@/router';
import { useLoadingStore } from '@/store';

export interface MenuProps {
  id?: number;
  name: string;
  path: string;
  title?: string;
  icon?: string;
  badge?: number | string;
  target?: '_self' | '_blank';
  link?: string;
  component: string;
  renderMenu?: boolean;
  permission?: string;
  parent?: string;
  children?: MenuProps[];
  cacheable?: boolean;
  view?: string;
  itemUrl?: string;
}

/**
 * 过滤菜单
 * @param routes
 * @param parentPermission
 */
function doMenuFilter(routes: Readonly<RouteRecordRaw[]>, parentPermission?: string) {
  const { hasAuthority } = useAuthStore();

  const setCache = (meta: RouteMeta) => {
    meta._cache = {
      renderMenu: meta.renderMenu,
    };
  };

  routes.forEach((route) => {
    const required = route.meta?.permission ?? parentPermission;
    // if (route.meta?.renderMenu === undefined && required) {
    if (required) {
      route.meta = route.meta ?? {};
      setCache(route.meta);
      route.meta.renderMenu = hasAuthority(required);
    }
    if (route.children) {
      doMenuFilter(route.children, required);
    }
  });
}

/**
 * 重置过滤
 * @param routes
 */
function resetMenuFilter(routes: Readonly<RouteRecordRaw[]>) {
  const resetCache = (meta: RouteMeta) => {
    if (meta._cache) {
      meta.renderMenu = meta._cache?.renderMenu;
    }
    delete meta._cache;
  };
  routes.forEach((route) => {
    if (route.meta) {
      resetCache(route.meta);
    }
    if (route.children) {
      resetMenuFilter(route.children);
    }
  });
}

// 菜单数据转为路由数据
const toRoutes = (list: MenuProps[]): RouteOption[] => {
  return list.map((item) => ({
    name: item.name,
    path: item.path,
    component: item.component,
    children: item.children && toRoutes(item.children),
    meta: {
      title: item.title,
      permission: item.permission,
      icon: item.icon,
      renderMenu: item.renderMenu,
      cacheable: false, //item.cacheable,
      href: item.link,
      badge: /^(false|true)$/i.test(item.badge + '') ? JSON.parse(item.badge + '') : item.badge,
      target: item.target,
      view: item.view,
      itemsUrl: item.itemUrl,
    },
  }));
};
const presetList: MenuProps[] = [
  {
    id: 1,
    name: 'overview',
    title: '概览',
    icon: 'DashboardOutlined',
    badge: '',
    target: '_self',
    path: '/overview',
    component: '@/pages/overview',
    renderMenu: true,
    parent: null,
    permission: null,
    cacheable: true,
  },
  {
    id: 2,
    name: 'team',
    title: '团队管理',
    icon: 'SettingOutlined',
    badge: '',
    target: '_self',
    path: '/team',
    component: '@/components/layout/BlankView.vue',
    renderMenu: true,
    parent: null,
    permission: null,
    cacheable: true,
  },
  {
    id: 3,
    name: 'user',
    title: '成员管理',
    target: '_self',
    path: '/team/user',
    component: '@/pages/teamManagement/user',
    renderMenu: true,
    parent: 'team',
    permission: null,
    cacheable: true,
  },
  {
    id: 31,
    name: 'role',
    title: '角色管理',
    target: '_self',
    path: '/team/role',
    component: '@/pages/teamManagement/role',
    renderMenu: true,
    parent: 'team',
    permission: null,
    cacheable: true,
  },
  {
    id: 4,
    name: 'fan',
    title: '粉丝运营',
    icon: 'SettingOutlined',
    badge: '',
    target: '_self',
    path: '/fan',
    component: '@/components/layout/BlankView.vue',
    renderMenu: true,
    parent: null,
    permission: null,
    cacheable: true,
  },
  {
    id: 5,
    name: 'fanslist',
    title: '粉丝管理',
    target: '_self',
    path: '/fan/fans',
    component: '@/pages/fanManagement/fans',
    renderMenu: true,
    parent: 'fan',
    permission: null,
  },
  {
    id: 51,
    name: 'fansInfo',
    title: '粉丝详情',
    target: '_self',
    path: '/fan/fans/:id',
    component: '@/pages/fanManagement/fans/FansInfo.vue',
    renderMenu: false,
    parent: 'fan',
    permission: null,
  },
  {
    id: 6,
    name: 'starlist',
    title: '明星管理',
    target: '_self',
    path: '/fan/stars',
    component: '@/pages/fanManagement/star',
    renderMenu: true,
    parent: 'fan',
    permission: null,
    cacheable: true,
  },
  {
    id: 61,
    name: 'starInfo',
    title: '明星详情',
    target: '_self',
    path: '/fan/stars/:id',
    component: '@/pages/fanManagement/star/StarInfo.vue',
    renderMenu: false,
    parent: 'fan',
    permission: null,
  },
  {
    id: 64,
    name: 'chat',
    title: '蜜聊',
    target: '_self',
    path: '/fan/stars/chat/:id',
    component: '@/pages/fanManagement/star/Chat.vue',
    renderMenu: false,
    parent: 'fan',
    permission: null,
  },
  {
    id: 66,
    name: 'with',
    title: '星伴',
    target: '_self',
    path: '/fan/stars/with/:id',
    component: '@/pages/fanManagement/star/With.vue',
    renderMenu: false,
    parent: 'fan',
    permission: null,
  },
  {
    id: 63,
    name: 'moments',
    title: '朋友圈',
    target: '_self',
    path: '/fan/stars/:id/moments/',
    component: '@/pages/fanManagement/star/Moments.vue',
    renderMenu: false,
    parent: 'fan',
    permission: null,
  },
  {
    id: 7,
    name: 'videoList',
    title: '视频管理',
    target: '_self',
    path: '/fan/videos',
    component: '@/pages/fanManagement/video',
    renderMenu: true,
    parent: 'fan',
    permission: null,
  },
  {
    id: 62,
    name: 'videoInfo',
    title: '视频详情',
    target: '_self',
    path: '/fan/videos/:id',
    component: '@/pages/fanManagement/video/VideoInfo.vue',
    renderMenu: false,
    parent: 'fan',
    permission: null,
    itemUrl: '',
  },
  {
    id: 8,
    name: 'commentList',
    title: '评论管理',
    target: '_self',
    path: '/fan/comments',
    component: '@/pages/fanManagement/comment',
    renderMenu: true,
    parent: 'fan',
    permission: null,
    cacheable: true,
  },
  {
    id: 9,
    name: 'finacial',
    title: '财务中心',
    icon: 'SettingOutlined',
    badge: '',
    target: '_self',
    path: '/finacial',
    component: '@/components/layout/BlankView.vue',
    renderMenu: true,
    parent: null,
    permission: null,
    cacheable: true,
  },
  {
    id: 10,
    name: 'recharge',
    title: '充值记录',
    target: '_self',
    path: '/finacial/recharge',
    component: '@/pages/finacialCenter/rechargeHistory',
    renderMenu: true,
    parent: 'finacial',
    permission: null,
    cacheable: true,
  },
  {
    id: 101,
    name: 'settlement',
    title: '分成收益',
    target: '_self',
    path: '/finacial/settlement',
    component: '@/pages/finacialCenter/settlementRevenue',
    renderMenu: true,
    parent: 'finacial',
    permission: 'admin',
    cacheable: true,
  },
  {
    id: 11,
    name: 'setting',
    title: '配置管理',
    icon: 'SettingOutlined',
    badge: '',
    target: '_self',
    path: '/setting',
    component: '@/components/layout/BlankView.vue',
    renderMenu: true,
    parent: null,
    permission: null,
    cacheable: true,
  },
  {
    id: 12,
    name: 'gift',
    title: '礼物配置',
    target: '_self',
    path: '/setting/gift',
    component: '@/pages/settingManagement/gift',
    renderMenu: true,
    parent: 'setting',
    permission: null,
    cacheable: true,
  },
  {
    id: 13,
    name: 'fanLevel',
    title: '粉丝等级配置',
    target: '_self',
    path: '/setting/fanLevel',
    component: '@/pages/settingManagement/fanLevel',
    renderMenu: true,
    parent: 'setting',
    permission: null,
    cacheable: true,
  },
  {
    id: 14,
    name: 'closenessLevel',
    title: '亲密度等级配置',
    target: '_self',
    path: '/setting/closenessLevel',
    component: '@/pages/settingManagement/closenessLevel',
    renderMenu: true,
    parent: 'setting',
    permission: null,
    cacheable: true,
  },
  {
    id: 15,
    name: 'interactionLevel',
    title: '互动经验配置',
    target: '_self',
    path: '/setting/interactionExp',
    component: '@/pages/settingManagement/interactionExp',
    renderMenu: true,
    parent: 'setting',
    permission: null,
    cacheable: true,
  },
  {
    id: 16,
    name: 'diamondConsume',
    title: '粉钻消耗配置',
    target: '_self',
    path: '/setting/diamonds',
    component: '@/pages/settingManagement/diamondConsume',
    renderMenu: true,
    parent: 'setting',
    permission: null,
    cacheable: true,
  },
  {
    id: 17,
    name: 'friendInvite',
    title: '邀请好友配置',
    target: '_self',
    path: '/setting/friend',
    component: '@/pages/settingManagement/friendInvite',
    renderMenu: true,
    parent: 'setting',
    permission: null,
    cacheable: true,
  },
  {
    id: 18,
    name: 'carousel',
    title: '轮播图配置',
    target: '_self',
    path: '/setting/carousel',
    component: '@/pages/settingManagement/carousel',
    renderMenu: true,
    parent: 'setting',
    permission: null,
    cacheable: true,
  },
  {
    id: 19,
    name: 'interactive',
    title: '互动活动',
    icon: 'InteractionOutlined',
    badge: '',
    target: '_self',
    path: '/interactive',
    component: '@/components/layout/BlankView.vue',
    renderMenu: true,
    parent: null,
    permission: null,
    cacheable: true,
  },
  {
    id: 20,
    name: 'bulletWallCreate',
    title: '新建弹幕墙',
    target: '_self',
    path: '/interactive/bulletwall/create',
    component: '@/pages/interactive/BulletWallCreate.vue',
    renderMenu: false,
    parent: 'interactive',
    permission: null,
    cacheable: true,
  },
  {
    id: 21,
    name: 'bulletWallList',
    title: '弹幕墙列表',
    path: '/interactive/bulletwall/list',
    component: '@/pages/interactive/BulletWallList.vue',
    renderMenu: true,
    parent: 'interactive',
    permission: null,
    cacheable: true,
  },
  {
    id: 22,
    name: 'bulletWallDetail',
    title: '弹幕墙详情',
    path: '/interactive/bulletwall/detail/:id',
    component: '@/pages/interactive/BulletWallDetail.vue',
    renderMenu: false,
    parent: 'interactive',
    permission: null,
    cacheable: true,
  },
  {
    id: 221,
    name: 'bulletWallEdit',
    title: '编辑弹幕墙',
    path: '/interactive/bulletwall/edit/:id',
    component: '@/pages/interactive/BulletWallEdit.vue',
    renderMenu: false,
    parent: 'interactive',
    permission: null,
    cacheable: true,
  },
  {
    id: 23,
    name: 'lifeFourGridList',
    title: '人生四格列表',
    target: '_self',
    path: '/interactive/lifefourGrid/list',
    component: '@/pages/interactive/LifeFourGridList.vue',
    renderMenu: true,
    parent: 'interactive',
    permission: null,
    cacheable: true,
  },
  {
    id: 24,
    name: 'lifeFourGridCreate',
    title: '新建人生四格',
    target: '_self',
    path: '/interactive/lifefourGrid/create',
    component: '@/pages/interactive/LifeFourGridCreate.vue',
    renderMenu: false,
    parent: 'interactive',
    permission: null,
    cacheable: true,
  },
  {
    id: 25,
    name: 'lifeFourGridEdit',
    title: '编辑人生四格',
    target: '_self',
    path: '/interactive/lifefourGrid/edit/:id',
    component: '@/pages/interactive/LifeFourGridEdit.vue',
    renderMenu: false,
    parent: 'interactive',
    permission: null,
    cacheable: true,
  },
  {
    id: 26,
    name: 'lifeFourGridDetail',
    title: '人生四格详情',
    target: '_self',
    path: '/interactive/lifefourGrid/detail/:id',
    component: '@/pages/interactive/LifeFourGridDetail.vue',
    renderMenu: false,
    parent: 'interactive',
    permission: null,
    cacheable: true,
  },
];

export const useMenuStore = defineStore('menu', () => {
  const menuList = ref<MenuProps[]>([]);

  const { filterMenu } = storeToRefs(useSettingStore());

  const checkMenuPermission = () => {
    // 始终执行权限过滤
    doMenuFilter(router.options.routes);
    console.log('菜单权限过滤完成', router.options.routes);
  };

  checkMenuPermission();

  watch(filterMenu, checkMenuPermission);

  async function getMenuList() {
    const { setPageLoading } = useLoadingStore();
    setPageLoading(true);

    const menuStr = localStorage.getItem('stepin-menu');
    if (!menuStr) {
      localStorage.setItem('stepin-menu', JSON.stringify(presetList));
    }

    let menuList2 = presetList;
    const menuMap = menuList2.reduce((p, c) => {
      p[c.name] = c;
      return p;
    }, {});
    menuList2.forEach((menu) => {
      menu.renderMenu = !!menu.renderMenu;
      if (menu.parent) {
        const parent = menuMap[menu.parent];
        parent.children = parent.children ?? [];
        parent.children.push(menu);
      }
    });
    menuList2 = menuList2.filter((menu) => !menu.parent);
    menuList.value = menuList2;
    replaceRoutes(toRoutes(menuList2), false);
    checkMenuPermission();

    setTimeout(() => {
      setPageLoading(false);
    }, 0);

    // return http
    //   .request<MenuProps[], Response<MenuProps[]>>('/barcode/menu', 'GET')
    //   .then((res) => {
    //     const { data } = res;
    //     menuList.value = data;
    //     replaceRoutes(toRoutes(data), false);
    //     checkMenuPermission();
    //     return data;
    //   })
    //   .finally(() => setPageLoading(false));
  }

  async function addMenu(menu: MenuProps) {
    return http
      .request<any, Response<any>>('/barcode/menu', 'POST_JSON', menu)
      .then((res) => {
        return res.data;
      })
      .finally(getMenuList);
  }

  async function updateMenu(menu: MenuProps) {
    return http
      .request<any, Response<any>>('/barcode/menu', 'PUT_JSON', menu)
      .then((res) => {
        return res.data;
      })
      .finally(getMenuList);
  }

  async function removeMenu(id: number) {
    return http
      .request<any, Response<any>>('/barcode/menu', 'DELETE', { id })
      .then(async (res) => {
        if (res.code === 0) {
          removeRoute(res.data.name);
        }
      })
      .finally(getMenuList);
  }

  return {
    menuList,
    getMenuList,
    addMenu,
    updateMenu,
    removeMenu,
  };
});
